import { useState } from "react";
import { Compass, ArrowRight, CheckCircle2 } from "lucide-react";
import { saveProgressReview, confirmProgressReview, completeCycle, getLifecycle } from "@/api";
import {
  PROGRESS_REVIEW_QUESTIONS, missingReviewFields, isReviewComplete, buildReviewPayload,
  canCompleteCycle, runSaveReview, runConfirmReview, runCompleteCycle,
} from "@/lib/progressReviewFlow";

const wrap = {
  maxWidth: 720, margin: "0 auto 1.5rem", padding: "1.8rem", background: "#0f1115",
  color: "#f4f4f5", borderRadius: 18, border: "1px solid #23262d",
  boxShadow: "0 18px 44px rgba(0,0,0,.3)",
};
const textareaStyle = {
  width: "100%", marginTop: 6, padding: "0.7rem 0.85rem", borderRadius: 10, minHeight: 90,
  background: "#16181d", color: "#f4f4f5", border: "1px solid #2a2e37", fontSize: "0.95rem",
  fontFamily: "inherit", resize: "vertical",
};

// Day 30 Progress Review → Confirm → explicit Final Completion. Self-contained: it holds the
// authoritative cycle state locally (seeded from the lifecycle) and refreshes it from the server
// after each canonical transition. It NEVER completes the cycle automatically.
export default function ProgressReview({ cycle, onCompleted }) {
  const [cyc, setCyc] = useState(cycle || {});
  const [note, setNote] = useState("");
  const [answers, setAnswers] = useState({});
  const [savedKey, setSavedKey] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);
  const [done, setDone] = useState(false);

  const payload = buildReviewPayload({ note, answers });
  const currentKey = JSON.stringify(payload);
  const complete = isReviewComplete({ note, answers });
  const missing = missingReviewFields({ note, answers });
  const ready = canCompleteCycle(cyc);
  const setAnswer = (k, v) => setAnswers((p) => ({ ...p, [k]: v }));

  const onSave = async () => {
    setBusy(true); setError("");
    const res = await runSaveReview({ payload, saveProgressReview: (p) => saveProgressReview(p, cycle && cycle.cycle_id) });
    setBusy(false);
    if (res.ok) {
      setSavedKey(currentKey);
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1500);
    } else setError(res.message);
  };

  const canConfirm = complete && savedKey === currentKey && !busy;

  const onConfirm = async () => {
    if (!canConfirm) return;
    setBusy(true); setError("");
    const res = await runConfirmReview({ confirmProgressReview: () => confirmProgressReview(cycle && cycle.cycle_id), getLifecycle });
    setBusy(false);
    if (res.ok) setCyc((res.lifecycle && res.lifecycle.current_cycle) || {});
    else setError(res.message);
  };

  const onComplete = async () => {
    if (!ready || busy) return;
    setBusy(true); setError("");
    const res = await runCompleteCycle({ completeCycle, getLifecycle, token: cyc.completion_action_token });
    setBusy(false);
    if (res.ok) {
      setCyc((res.lifecycle && res.lifecycle.current_cycle) || {});
      setDone(true);
      if (onCompleted) onCompleted(res.lifecycle);
    } else setError(res.message);
  };

  // Phase C — completed (terminal, read-only).
  if (done) {
    return (
      <div data-testid="progress-review-completed" style={wrap}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#2dd4bf", fontWeight: 700 }}>
          <CheckCircle2 size={18} /> Findability Cycle complete
        </div>
        <p style={{ color: "#a1a1aa", marginTop: 10, fontSize: ".95rem" }}>
          Your cycle is now read-only. You can still review all of your work — nothing is lost.
        </p>
      </div>
    );
  }

  // Phase B — ready to complete: explicit Final Completion only.
  if (ready) {
    return (
      <div data-testid="progress-review-ready" style={wrap}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#2dd4bf",
                      fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", fontSize: 12 }}>
          <CheckCircle2 size={16} /> Progress Review confirmed
        </div>
        <h2 style={{ fontSize: "1.5rem", margin: ".5rem 0 .3rem" }}>You're ready to close this cycle</h2>
        <p style={{ color: "#a1a1aa", margin: 0, fontSize: ".95rem" }}>
          Your cycle will become read-only after completion. You'll still be able to review your work.
        </p>
        <button
          type="button"
          data-testid="complete-cycle-btn"
          onClick={onComplete}
          disabled={busy}
          style={{ marginTop: "1.2rem", display: "inline-flex", alignItems: "center", gap: 10,
                   padding: "0.85rem 1.4rem", borderRadius: 999, border: "none",
                   cursor: busy ? "not-allowed" : "pointer",
                   background: "#2dd4bf", color: "#04231f", fontWeight: 700, fontSize: "1rem" }}
        >
          {busy ? "Completing…" : "Complete My Findability Cycle"} <ArrowRight size={18} />
        </button>
        {error && <div data-testid="progress-review-error" style={{ marginTop: 14, color: "#f4a3b0", fontSize: ".9rem" }}>{error}</div>}
      </div>
    );
  }

  // Phase A — Progress Review entry (note + q1..q4) → Save → Confirm.
  return (
    <div data-testid="progress-review" style={wrap}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: "#2dd4bf",
                    fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", fontSize: 12 }}>
        <Compass size={16} /> Progress Review
      </div>
      <h2 data-testid="progress-review-title" style={{ fontSize: "1.6rem", margin: ".5rem 0 .35rem" }}>
        Progress Review
      </h2>
      <p data-testid="progress-review-intro" style={{ color: "#a1a1aa", margin: 0, fontSize: ".95rem" }}>
        You completed 30 focused efforts. Before closing this Findability Cycle, take a moment to
        compare what changed.
      </p>

      <div style={{ marginTop: "1.4rem" }}>
        <label htmlFor="reviewNote" style={{ fontSize: ".8rem", color: "#c7c7cf", fontWeight: 600 }}>
          What I found now
        </label>
        <textarea
          id="reviewNote"
          data-testid="progress-review-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g., I now show up for two of my phrases, with my own method named — a real change from my baseline."
          style={textareaStyle}
        />
      </div>

      <div data-testid="progress-review-questions" role="group" aria-label="Progress self-check questions"
           style={{ marginTop: "1.3rem", display: "grid", gap: "1rem" }}>
        {PROGRESS_REVIEW_QUESTIONS.map((q) => (
          <div key={q.key} data-testid={`progress-review-${q.key}`}>
            <div style={{ fontSize: ".92rem", color: "#e4e4e7", marginBottom: 8 }}>{q.text}</div>
            <div style={{ display: "flex", gap: 10 }}>
              {["yes", "no"].map((val) => {
                const selected = answers[q.key] === val;
                return (
                  <button
                    key={val}
                    type="button"
                    data-testid={`progress-review-${q.key}-${val}`}
                    aria-pressed={selected}
                    onClick={() => setAnswer(q.key, val)}
                    style={{
                      padding: "0.5rem 1.3rem", borderRadius: 999, cursor: "pointer",
                      fontWeight: 600, fontSize: ".9rem",
                      border: selected ? "1px solid #2dd4bf" : "1px solid #2a2e37",
                      background: selected ? "#0d2e2b" : "#16181d",
                      color: selected ? "#2dd4bf" : "#a1a1aa",
                    }}
                  >
                    {val === "yes" ? "Yes" : "No"}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!complete && (
        <div data-testid="progress-review-validation"
             style={{ marginTop: "1.1rem", padding: "0.85rem 1rem", borderRadius: 12,
                      background: "#1a1410", border: "1px solid #4a2f17", color: "#f5c078", fontSize: ".9rem" }}>
          Add your findings and answer all four questions before you can confirm.
        </div>
      )}

      <div style={{ marginTop: "1.4rem", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
        <button
          type="button"
          data-testid="progress-review-save-btn"
          onClick={onSave}
          disabled={busy}
          style={{ padding: "0.8rem 1.3rem", borderRadius: 999, cursor: busy ? "not-allowed" : "pointer",
                   border: "1px solid #2a2e37", background: "#16181d", color: "#e4e4e7", fontWeight: 600, fontSize: ".95rem" }}
        >
          Save Progress Review
        </button>
        <button
          type="button"
          data-testid="progress-review-confirm-btn"
          onClick={onConfirm}
          disabled={!canConfirm}
          style={{ display: "inline-flex", alignItems: "center", gap: 10,
                   padding: "0.85rem 1.4rem", borderRadius: 999, border: "none",
                   cursor: canConfirm ? "pointer" : "not-allowed",
                   background: canConfirm ? "#2dd4bf" : "#264c48", color: canConfirm ? "#04231f" : "#7dd3cf",
                   fontWeight: 700, fontSize: "1rem" }}
        >
          {busy ? "Working…" : "Confirm Progress Review"} <ArrowRight size={18} />
        </button>
        <span data-testid="progress-review-save-status" style={{ color: "#71717a", fontSize: ".82rem" }}>
          {savedFlash ? "✓ Saved" : complete && savedKey !== currentKey ? "Save, then confirm." : ""}
        </span>
      </div>

      {error && (
        <div data-testid="progress-review-error"
             style={{ marginTop: "1.2rem", padding: "0.85rem 1rem", borderRadius: 12,
                      background: "#1c1417", border: "1px solid #4a1f27", color: "#f4a3b0", fontSize: ".9rem" }}>
          {error}
        </div>
      )}
    </div>
  );
}
